import axios from 'axios';
import api, { SERVER_URL } from './api';

/**
 * How the meeting room talks to the server.
 *
 * Employees and outside guests sit in the same room screen, but they reach the
 * server differently - employees with their sign-in, guests with the token from
 * their invitation. The room only knows this small interface, so it does not
 * care which it has been given.
 *
 * Guests use a bare axios instance, not utils/api.js: that one attaches an
 * employee token and sends anyone with a stale one to the login page, neither
 * of which a guest should ever trigger.
 */

const unwrap = (res) => res.data;

export const employeeMeetingApi = (meetingId) => ({
    listMessages: (after) => api.get(`/meetings/${meetingId}/messages`, { params: { after } }).then(unwrap),
    sendMessage: (text) => api.post(`/meetings/${meetingId}/messages`, { text }).then(unwrap),
    // "May I share my screen?" - one at a time; see server/utils/meetingShare.js.
    requestShare: () => api.post(`/meetings/${meetingId}/share-request`).then(unwrap),
    // Host controls. Guests are never hosts, so the guest interface has none.
    mute: (identity) => api.post(`/meetings/${meetingId}/mute`, { identity }).then(unwrap),
    remove: (identity) => api.post(`/meetings/${meetingId}/remove`, { identity }).then(unwrap),
    setLock: (locked) => api.post(`/meetings/${meetingId}/lock`, { locked }).then(unwrap),
    end: () => api.post(`/meetings/${meetingId}/end`).then(unwrap),
    // More time for a meeting that is running over: 15 or 30 minutes.
    extend: (minutes) => api.post(`/meetings/${meetingId}/extend`, { minutes }).then(unwrap),
    lobby: () => api.get(`/meetings/${meetingId}/lobby`).then(unwrap),
    decide: (entryId, decision) => api.post(`/meetings/${meetingId}/lobby/${entryId}/${decision}`).then(unwrap)
});

const guestClient = axios.create({ baseURL: `${SERVER_URL}/api/meet` });

export const guestMeetingApi = (token) => ({
    listMessages: (after) => guestClient.get(`/${token}/messages`, { params: { after } }).then(unwrap),
    sendMessage: (text) => guestClient.post(`/${token}/messages`, { text }).then(unwrap),
    requestShare: () => guestClient.post(`/${token}/share-request`, {}).then(unwrap)
});

/** The public (no sign-in) calls the guest page makes before there is a room. */
export const guestPublic = {
    info: (token) => guestClient.get(`/${token}`).then(unwrap),
    rsvp: (token, rsvp) => guestClient.post(`/${token}/rsvp`, { rsvp }).then(unwrap),
    join: (token, body) => guestClient.post(`/${token}/join`, body).then(unwrap),
    byCode: (code) => guestClient.get(`/code/${code}`).then(unwrap),
    requestEntry: (code, body) => guestClient.post(`/code/${code}/request`, body).then(unwrap),
    waitStatus: (waitToken) => guestClient.get(`/wait/${waitToken}`).then(unwrap)
};
